import useCart from '@/store/cart.store';
import useShippingInfoStore from '@/store/shipping-info.store';
import { event_begin_checkout } from '@/utils/gtm';
import { getUnavailableSkus } from '@/utils/gtm/item-lists';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from '@/i18n/navigation';
import { getCookie } from 'cookies-next';
import { SHIPPING_INFO_MODALS_NAMES } from '@/constants';
import { signIn } from 'next-auth/react';

const RedirectToCheckout = ({ type, togglePopup, toggleDrawer }) => {
  const {
    shippingMethod,
    shippingAddress,
    unavailableItems,
    setIsOpenShippingInfoModal,
    setActiveStep,
  } = useShippingInfoStore();

  const t = useTranslations('cart');
  const locale = useLocale();
  const { push } = useRouter();
  const mageCookie = getCookie('mage');

  const cartItems = useCart((state) => state.cartItems);
  const cart = useCart((state) => state.cart);
  const couponCode = cart?.coupon_code;

  // The checkout attempt is the click that actually proceeds. The branches below
  // that only open a shipping modal are a precondition prompt, not an attempt,
  // so they report nothing.
  const startCheckout = () =>
    event_begin_checkout({
      cartLines: cartItems,
      cart,
      coupon: couponCode,
      locale,
      unavailableSkus: getUnavailableSkus(unavailableItems),
    });

  const handleRedirectToCheckout = () => {
    if (mageCookie) {
      if (
        shippingMethod === SHIPPING_INFO_MODALS_NAMES.HOME_DELIVERY &&
        !shippingAddress?.id
      ) {
        setIsOpenShippingInfoModal(true);
        setActiveStep(SHIPPING_INFO_MODALS_NAMES.DELIVERY_ADDRESS);
        return;
      } else if (
        shippingMethod === SHIPPING_INFO_MODALS_NAMES.STORE_PICKUP &&
        !shippingAddress?.id
      ) {
        setIsOpenShippingInfoModal(true);
        setActiveStep(SHIPPING_INFO_MODALS_NAMES.CHOOSE_BRANCH);
        return;
      } else if (!shippingMethod) {
        setIsOpenShippingInfoModal(true);
        setActiveStep(SHIPPING_INFO_MODALS_NAMES.SHIPPING_METHOD);
        return;
      } else {
        startCheckout();
        push('/checkout');
      }
    } else {
      // Fires before the guest is sent to login/OTP.
      startCheckout();
      signIn(undefined, { callbackUrl: '/checkout' });
    }
  };

  const commonClickHandler = () => {
    handleRedirectToCheckout();
    togglePopup?.();
    toggleDrawer?.();
  };

  const buttonsConfig = {
    fromCart: {
      className:
        'bg-primary hover:bg-primary-900 flex h-8.75 w-full items-center justify-center rounded-md text-[15px] font-bold text-white max-sm:text-sm max-sm:text-[13px] lg:h-11.25 lg:rounded-[10px]',
      onClick: handleRedirectToCheckout,
    },
    fromCartResponsive: {
      className:
        'bg-primary hover:bg-primary-900 mb-3 flex h-9 w-full items-center justify-center rounded-md text-sm font-bold text-white',
      onClick: handleRedirectToCheckout,
    },
    fromFooter: {
      className:
        'w-full cursor-pointer appearance-none rounded-[14px] border-[#272b6f] bg-[#272b6f] py-3 text-sm font-bold text-white transition duration-200 hover:border-[#1b205a] hover:bg-[#1b205a] md:px-6',
      onClick: commonClickHandler,
    },
    fromSmallCart: {
      className:
        'bg-primary rounded-[12px] p-2.5 text-sm font-bold text-white max-md:w-[55%] md:px-[14px] md:py-[10px] md:text-base',
      onClick: () => {
        handleRedirectToCheckout();
        togglePopup?.();
      },
      id: 'pillCheckout',
    },
    fromCtaButtons: {
      className:
        'bg-primary flex w-[70%] cursor-pointer items-center justify-center gap-1 rounded-[10px] px-[14px] py-[10px] text-[14px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-40',
      onClick: handleRedirectToCheckout,
    },
    fromMiniCart: {
      className:
        'hover:border-rd-400 border-primary bg-primary hover:border-primary/80 hover:bg-primary-900 mb-3 flex w-full cursor-pointer items-center justify-center rounded-md border p-1.5 font-semibold text-white',
      onClick: handleRedirectToCheckout,
    },
  };

  const config = buttonsConfig[type];

  if (!config) return null;

  return (
    <button
      {...(config.id && { id: config.id })}
      className={config.className}
      onClick={config.onClick}
    >
      {t('confirmBuy')}
    </button>
  );
};

export default RedirectToCheckout;
